import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import DocRenderer from "./DocRenderer";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import images from "../constants/image.js";
import { Navbar } from "./Navbar.jsx";

const pics = [
  {
    logo: images.PB3,
  },
];

function PartScore() {
  const navigate = useNavigate();
  const [showResume, setShowResume] = useState(true);
  const [currentResumePage, setCurrentResumePage] = useState(1); // Track page for resume
  const [currentJobDescPage, setCurrentJobDescPage] = useState(1); // Track page for job description
  const location = useLocation();
  const { data, jobDescriptionFile, jobDescriptionText, resumeFile } = location.state || {};

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      setScreenHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleContinue = () => {
    const completedSteps = JSON.parse(localStorage.getItem("completedSteps")) || [];
    if (!completedSteps.includes("partScore")) {
      completedSteps.push("partScore");
      localStorage.setItem("completedSteps", JSON.stringify(completedSteps));
    }
    navigate("/pay", { state: { data, jobDescriptionFile, jobDescriptionText, resumeFile } });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  let score = data?.score || 0;
  let scoreInt = Math.round(score);
  const totalMissingKeywords = data?.missing_keywords_length || 0;
  const visibleKeywords = data?.partial_missing_keywords || [];
  const numBlurredKeywords = totalMissingKeywords - visibleKeywords.length;
  const sessionId = data ? data.session_id : null;
  localStorage.setItem('sessionId',sessionId);
  const message = scoreInt > 70 ? 'Excellent' : 'Needs Improvement';

  return (
    <>
    <Navbar />
    <div className="flex flex-col min-h-screen bg-bellGrey md:px-8">
      <main className="flex flex-col px-4">
        <div className="flex justify-center w-full mb-4">
          <img src={pics[0].logo} alt="Progress Bar" />
        </div>

        <h1 className="text-center text-lg font-medium text-charcoal">Resume Score</h1>

        <div className="flex flex-col lg:flex-row gap-8 mt-10">
          <div className="flex-grow flex flex-col gap-4 lg:w-1/2">
            <div className="bg-white shadow-md rounded-lg p-6 text-center">
              <div className="flex justify-center items-center">
                <div className="relative w-40 h-40">
                  <div className="absolute rounded-full border-4 bg-white border-gray-300"></div>
                  <div
                    className="absolute w-full h-full rounded-full border-4 bg-white border-green-500"
                    style={{
                      clipPath: "circle(50% at 50% 50%)",
                      background: `conic-gradient(
                        #4ade80 ${scoreInt * 3.6}deg, 
                        #e5e7eb ${(100-scoreInt) * 3.6}deg
                      )`,
                    }}
                  ></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xl font-bold text-black">{scoreInt}%</span>
                  </div>
                </div>
              </div>
              <h2 className="text-xl font-semibold text-charcoal mt-4">{message}</h2>
              <p className="text-black mt-2">
                Keep improving your resume to achieve even better results and opportunities.
              </p>
            </div>

            <div className="bg-white shadow-md rounded-lg p-6 text-center">
              <h3 className="text-lg font-semibold text-charcoal">Missing Keywords</h3>
              <ul className="mt-4 flex flex-wrap justify-center items-center gap-4">
  {visibleKeywords.map((keyword, index) => (
    <li
      key={index}
      className="flex items-center space-x-2 bg-gray-100 px-4 py-2 rounded-md shadow-sm"
    >
      <span className="text-black font-medium">{keyword}</span>
      <span className="text-green-500">✅</span>
    </li>
  ))}
                {[...Array(numBlurredKeywords)].map((_, index) => (
                  <li
                    key={`blur-${index}`}
                    className="text-black"
                    style={{ filter: "blur(5px)" }}
                  >
                    Hidden Keyword
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white shadow-md rounded-lg p-6 text-center">
              <p>
                Improve your resume match score by discovering all the missing keywords
                recommended.
              </p>
              <button
                className="py-2 px-6 mt-4 rounded-lg bg-blue-600 text-white font-medium hover:bg-orange-500 shadow-md transition-colors duration-300"
                onClick={handleContinue}
              >
                Pay here
              </button>
            </div>
          </div>
          <div className="flex-grow lg:w-1/2">
            <div className="bg-white shadow-md rounded-lg p-6">
              <div className="flex justify-between mb-4">
                <button
                  className={`py-2 px-4 w-full lg:w-auto rounded-lg ${
                    showResume
                      ? "bg-blue-600 text-white hover:bg-orange-500"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  onClick={() => {
                    setShowResume(true);
                    setCurrentResumePage(1);
                  }}
                >
                  My Resume
                </button>
                <button
                  className={`py-2 px-4 w-full lg:w-auto rounded-lg ${
                    !showResume
                      ? "bg-blue-600 text-white hover:bg-orange-500"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                  onClick={() => {
                    setShowResume(false);
                    setCurrentJobDescPage(1);
                  }}
                >
                  Job Description
                </button>
              </div>
              <div className="h-[60vh] overflow-auto border border-charcoal border-opacity-10 rounded-lg p-4">
                {showResume ? (
                  resumeFile ? (
                    <DocRenderer
                      file={resumeFile}
                      currentPage={currentResumePage}
                      key={`${screenWidth}-${screenHeight}`}
                    />
                  ) : (
                    <div>No resume uploaded.</div>
                  )
                ) : jobDescriptionFile ? (
                  <DocRenderer
                    file={jobDescriptionFile}
                    currentPage={currentJobDescPage}
                    key={`${screenWidth}-${screenHeight}`}
                  />
                ) : jobDescriptionText ? (
                  <div>{jobDescriptionText}</div>
                ) : (
                  <div>No job description uploaded.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
    </>
  );
}

export default PartScore;
